import { useMemo } from 'react'
import { getAllWaypointsFromTrip, resolveMeetupDisplay } from '@/modules/trips/utils/meetupWaypointSync'
import { Clock } from '@/ui/icons'
import { ShareLinkPermissions } from '@/shared/api/shareLinks'
import { InviteCodePermissions } from '@/shared/api/inviteCodes'
import { TripWithDetailsResponse } from '@/shared/api'
import { Card } from '@/ui/components'
import { formatDateForDisplay } from '@/modules/trips/utils/dateHelpers'

interface SharePreviewProps {
  trip: TripWithDetailsResponse | null
  permissions: ShareLinkPermissions | InviteCodePermissions
  isLoading?: boolean
  isInviteCode?: boolean
}

/**
 * Filters trip data based on permissions, matching backend tripAccessService.filterTripData() logic
 */
function filterTripDataForPreview(
  trip: TripWithDetailsResponse | null,
  permissions: ShareLinkPermissions | InviteCodePermissions,
  isInviteCode: boolean = false
): any {
  if (!trip) return null

  const filtered = { ...trip }
  const isInvite = isInviteCode && 'viewTripDetails' in permissions
  const invitePerms = isInvite ? permissions as InviteCodePermissions : null
  const sharePerms = !isInvite ? permissions as ShareLinkPermissions : null

  if (isInvite && invitePerms && !invitePerms.viewTripDetails) {
    filtered.description = null
  }

  if (isInvite && invitePerms) {
    if (!invitePerms.viewGear) {
      (filtered as any).gear = []
    } else {
      if (!(filtered as any).gear) {
        (filtered as any).gear = []
      }
    }
  }

  if (isInvite && invitePerms) {
    if (!invitePerms.viewMeals) {
      (filtered as any).meals = []
    } else {
      if (!(filtered as any).meals) {
        (filtered as any).meals = []
      }
    }
  }

  const isMeetupSpotWaypoint = (wp: any): boolean => {
    const waypointData = wp?.waypoint ?? wp
    return waypointData?.poi_type === 'meetup_spot'
  }

  const viewItinerary = isInvite ? invitePerms?.viewItinerary : sharePerms?.viewItinerary
  const viewMeetupLocation = isInvite ? invitePerms?.viewMeetupLocation : sharePerms?.viewMeetupLocation
  if (!viewItinerary) {
    filtered.waypoints = []
  } else {
    const viewCampsiteCoordinates = isInvite ? invitePerms?.viewCampsiteCoordinates : sharePerms?.viewCampsiteCoordinates
    if (!viewCampsiteCoordinates && filtered.waypoints) {
      filtered.waypoints = filtered.waypoints.map((wp: any) => {
        const waypointData = wp.waypoint || wp
        if (waypointData) {
          if (wp.waypoint) {
            return {
              ...wp,
              waypoint: {
                ...waypointData,
                latitude: null,
                longitude: null
              }
            }
          } else {
            return {
              ...wp,
              latitude: null,
              longitude: null
            }
          }
        }
        return wp
      })
    }

    if (!viewMeetupLocation && filtered.waypoints) {
      filtered.waypoints = filtered.waypoints.filter((wp: any) => !isMeetupSpotWaypoint(wp))
    }
  }

  const viewCrew = isInvite ? invitePerms?.viewCrew : sharePerms?.viewCrew
  if (!viewCrew) {
    filtered.members = []
  }

  const viewAnnouncements = isInvite
    ? invitePerms?.viewAnnouncements ?? (invitePerms as { viewNotes?: boolean })?.viewNotes
    : sharePerms?.viewAnnouncements ?? (sharePerms as { viewNotes?: boolean })?.viewNotes
  if (!viewAnnouncements) {
    filtered.announcements = []
  }

  const viewRadioChannel = isInvite ? invitePerms?.viewRadioChannel : sharePerms?.viewRadioChannel
  if (!viewRadioChannel) {
    filtered.radio_channel = null
    filtered.radio_frequency = null
    filtered.radio_type = null
  }

  if (!viewMeetupLocation) {
    filtered.meetup_location = null
    filtered.meetup_time = null
  }

  return filtered
}

export function SharePreview({ trip, permissions, isLoading, isInviteCode = false }: SharePreviewProps) {
  const filteredTrip = useMemo(() => {
    return filterTripDataForPreview(trip, permissions, isInviteCode)
  }, [trip, permissions, isInviteCode])

  const isInvite = isInviteCode && 'viewTripDetails' in permissions
  const invitePerms = isInvite ? permissions as InviteCodePermissions : null
  const sharePerms = !isInvite ? permissions as ShareLinkPermissions : null

  if (isLoading) {
    return (
      <Card className="p-4">
        <p className="text-xs font-medium text-muted-foreground mb-4">
          {isInviteCode ? 'Guest view' : 'What recipients will see'}
        </p>
        <div className="space-y-3">
          <div className="h-4 bg-earth-200 rounded animate-pulse" />
          <div className="h-4 bg-earth-200 rounded animate-pulse w-3/4" />
          <div className="h-20 bg-earth-200 rounded animate-pulse" />
        </div>
      </Card>
    )
  }

  if (!trip) {
    return (
      <Card className="p-4">
        <p className="text-xs font-medium text-muted-foreground mb-4">
          {isInviteCode ? 'Guest view' : 'What recipients will see'}
        </p>
        <p className="text-sm text-muted-foreground">Trip data not available</p>
      </Card>
    )
  }

  const viewItinerary = isInvite ? invitePerms?.viewItinerary : sharePerms?.viewItinerary
  const viewCrew = isInvite ? invitePerms?.viewCrew : sharePerms?.viewCrew
  const viewAnnouncements = isInvite
    ? invitePerms?.viewAnnouncements ?? (invitePerms as { viewNotes?: boolean })?.viewNotes
    : sharePerms?.viewAnnouncements ?? (sharePerms as { viewNotes?: boolean })?.viewNotes
  const viewRadioChannel = isInvite ? invitePerms?.viewRadioChannel : sharePerms?.viewRadioChannel
  const viewMeetupLocation = isInvite ? invitePerms?.viewMeetupLocation : sharePerms?.viewMeetupLocation
  const viewGear = isInvite ? invitePerms?.viewGear : false
  const viewMeals = isInvite ? invitePerms?.viewMeals : false

  const viewCampsiteCoordinates = isInvite ? invitePerms?.viewCampsiteCoordinates : sharePerms?.viewCampsiteCoordinates

  const hasItinerary = viewItinerary && filteredTrip?.waypoints && filteredTrip.waypoints.length > 0
  const hasCrew = viewCrew && filteredTrip?.members && filteredTrip.members.length > 0
  const announcementsList = filteredTrip?.announcements ?? []
  const hasAnnouncements = Boolean(viewAnnouncements && announcementsList.length > 0)
  const hasRadioChannel = viewRadioChannel && (trip.radio_channel || trip.radio_frequency)

  const meetupPreview = useMemo(
    () => (trip ? resolveMeetupDisplay(trip, getAllWaypointsFromTrip(trip)) : null),
    [trip]
  )
  const hasMeetupLocation = viewMeetupLocation && Boolean(meetupPreview?.locationLabel)
  const hasGear = viewGear && filteredTrip?.gear && filteredTrip.gear.length > 0
  const hasMeals = viewMeals && filteredTrip?.meals && filteredTrip.meals.length > 0

  return (
    <Card className="p-4">
      <p className="text-xs font-medium text-muted-foreground mb-4">
        {isInviteCode ? 'Guest view' : 'What recipients will see'}
      </p>

      <div className="space-y-4">
        {/* Trip header — always visible */}
        <div>
          <p className="text-sm font-medium">{trip.name}</p>
          {trip.start_date && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDateForDisplay(trip.start_date)}
              {trip.end_date && ` – ${formatDateForDisplay(trip.end_date)}`}
            </p>
          )}
        </div>

        {/* Gear (invite codes only) */}
        {isInviteCode && viewGear && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Gear</span>
            {hasGear ? (
              <div className="space-y-1">
                {filteredTrip.gear.slice(0, 3).map((gear: any, idx: number) => (
                  <p key={gear.id || idx} className="text-xs text-muted-foreground">
                    {gear.name || 'Unnamed gear'}
                    {gear.category && <span className="text-earth-500"> ({gear.category})</span>}
                  </p>
                ))}
                {filteredTrip.gear.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{filteredTrip.gear.length - 3} more
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No gear in this trip</p>
            )}
          </div>
        )}

        {/* Meals (invite codes only) */}
        {isInviteCode && viewMeals && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Meals</span>
            {hasMeals ? (
              <div className="space-y-1">
                {filteredTrip.meals.slice(0, 3).map((meal: any, idx: number) => (
                  <p key={meal.id || idx} className="text-xs text-muted-foreground">
                    {meal.meal_name || meal.name || 'Unnamed meal'}
                    {meal.planned_date && (
                      <span className="text-earth-500"> ({new Date(meal.planned_date).toLocaleDateString()})</span>
                    )}
                  </p>
                ))}
                {filteredTrip.meals.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{filteredTrip.meals.length - 3} more
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No meals in this trip</p>
            )}
          </div>
        )}

        {/* Itinerary */}
        {viewItinerary && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">Itinerary</span>
              {!viewCampsiteCoordinates && (
                <span className="text-xs text-muted-foreground">(coordinates hidden)</span>
              )}
            </div>
            {hasItinerary ? (
              <div className="space-y-1">
                {filteredTrip.waypoints.slice(0, 3).map((wp: any, idx: number) => {
                  const waypoint = wp.waypoint || wp
                  const hasCoords = waypoint.latitude && waypoint.longitude
                  return (
                    <div key={wp.id || idx} className="text-xs text-muted-foreground">
                      <span className="font-medium">{waypoint.name || 'Unnamed waypoint'}</span>
                      {waypoint.poi_type === 'meetup_spot' && viewMeetupLocation && (
                        <span className="ml-1.5 text-[10px] font-medium text-adventure-800 bg-adventure-100 px-1.5 py-0.5 rounded">
                          Meetup
                        </span>
                      )}
                      {hasCoords && viewCampsiteCoordinates && (
                        <span className="ml-2 text-earth-500">
                          ({waypoint.latitude?.toFixed(4)}, {waypoint.longitude?.toFixed(4)})
                        </span>
                      )}
                    </div>
                  )
                })}
                {filteredTrip.waypoints.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{filteredTrip.waypoints.length - 3} more
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No waypoints in this trip</p>
            )}
          </div>
        )}

        {/* Crew */}
        {viewCrew && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Participants</span>
            {hasCrew ? (
              <div className="space-y-1">
                {filteredTrip.members.slice(0, 3).map((member: any, idx: number) => (
                  <p key={member.id || idx} className="text-xs text-muted-foreground">
                    <span className="font-medium">
                      {member.guest_name || member.user?.email?.split('@')[0] || 'Member'}
                    </span>
                    {member.role && <span className="text-earth-500"> ({member.role})</span>}
                  </p>
                ))}
                {filteredTrip.members.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{filteredTrip.members.length - 3} more
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No participants in this trip</p>
            )}
          </div>
        )}

        {/* Announcements */}
        {viewAnnouncements && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Announcements</span>
            {hasAnnouncements ? (
              <div className="space-y-1">
                {announcementsList.slice(0, 3).map((announcement: { id: string; content: string }) => (
                  <p key={announcement.id} className="text-xs text-muted-foreground line-clamp-2">
                    {announcement.content}
                  </p>
                ))}
                {announcementsList.length > 3 && (
                  <p className="text-xs text-muted-foreground italic">
                    +{announcementsList.length - 3} more
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No announcements in this trip</p>
            )}
          </div>
        )}

        {/* Radio Channel */}
        {viewRadioChannel && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Radio Channel</span>
            {hasRadioChannel ? (
              <div className="space-y-0.5">
                {trip.radio_channel && (
                  <p className="text-xs text-muted-foreground font-medium">{trip.radio_channel}</p>
                )}
                {trip.radio_frequency && (
                  <p className="text-xs text-muted-foreground">
                    {trip.radio_frequency}{trip.radio_type && ` · ${trip.radio_type}`}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No radio channel set</p>
            )}
          </div>
        )}

        {/* Meetup Location */}
        {viewMeetupLocation && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Meetup Location</span>
            {hasMeetupLocation && meetupPreview ? (
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground font-medium">{meetupPreview.locationLabel}</p>
                {meetupPreview.timeFormatted && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    {meetupPreview.timeFormatted}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No meetup location set</p>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
